import { Fragment } from 'react';
import { clsx } from 'clsx';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Cell = string | 'check' | 'dash';

type Row = { label: string; values: readonly [Cell, Cell, Cell, Cell, Cell] };

type Category = { title: string; rows: ReadonlyArray<Row> };

const PLANS = ['Gratuit', 'Starter', 'Croissance', 'Pro', 'Business'] as const;
const PRICES = ['0 F', '5K F', '10K F', '20K F', 'Sur devis'] as const;

const CATEGORIES: ReadonlyArray<Category> = [
  {
    title: 'Volumes',
    rows: [
      { label: 'Boutiques', values: ['1', '1', '2', '5', 'Illimité'] },
      { label: 'Produits', values: ['5', '50', '1 000', 'Illimité', 'Illimité'] },
      { label: 'Employés', values: ['1', '2', '5', '15', 'Illimité'] },
      { label: 'Dépôts', values: ['1', '1', '2', '5', 'Illimité'] },
      { label: 'Caisses desktop', values: ['1', '1', '3', '5', 'Illimité'] },
      { label: 'Appareils mobile', values: ['1', '1', '3', '5', 'Illimité'] },
      {
        label: 'Historique ventes',
        values: ['3 mois', '6 mois', '12 mois', '36 mois', 'Illimité'],
      },
    ],
  },
  {
    title: 'Modules inclus',
    rows: [
      { label: 'Caisse POS (offline)', values: ['check', 'check', 'check', 'check', 'check'] },
      {
        label: 'Gestion stock multi-dépôt',
        values: ['Basique', 'Simple', 'check', 'check', 'check'],
      },
      { label: 'Mobile Money', values: ['dash', 'dash', 'check', 'check', 'check'] },
      { label: 'Rapports avancés', values: ['dash', 'dash', 'Exports', 'check', 'check'] },
      { label: 'API publique', values: ['dash', 'dash', 'dash', 'dash', 'check'] },
      { label: 'Prévisions IA', values: ['dash', 'dash', 'dash', 'check', 'check'] },
      { label: 'Crédits IA / mois', values: ['0', '0', '30', '100', '200'] },
    ],
  },
  {
    title: 'Support',
    rows: [
      { label: 'Support inclus', values: ['Communauté', 'Email', 'Chat', 'Chat', 'Négocié'] },
      { label: 'Temps de réponse', values: ['72h', '48h', '24h', '24h', 'SLA'] },
      { label: 'Account manager dédié', values: ['dash', 'dash', 'dash', 'dash', 'check'] },
      { label: 'Formation', values: ['Base publique', 'Vidéos', 'Vidéos', 'Visio', 'Sur place'] },
    ],
  },
];

function CellContent({ value, featured }: { value: Cell; featured: boolean }) {
  const text = clsx(
    'font-marketing-body text-[14px]',
    featured ? 'font-bold text-charcoal-800' : 'font-medium text-charcoal-700',
  );
  if (value === 'check') {
    return (
      <span
        className={`inline-flex h-6 w-6 items-center justify-center border-2 border-charcoal-800 ${featured ? 'bg-lime-500' : 'bg-white'}`}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.8"
        >
          <path d="M2.5 6l2.5 2.5 5-5" />
        </svg>
      </span>
    );
  }
  if (value === 'dash') {
    return <span className={clsx(text, 'text-charcoal-300')}>—</span>;
  }
  return <span className={text}>{value}</span>;
}

export default function PricingCompareTableV2() {
  return (
    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel className="justify-center">Comparaison détaillée</SectionLabel>
          <h2 className="mt-6 font-marketing-display text-[clamp(32px,4.2vw,54px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
            Ce qui change <span className="underline-lime">entre les plans</span>.
          </h2>
          <p className="mt-5 font-marketing-body text-lg text-charcoal-600">
            Les petits plans restent volontairement simples. Les volumes et les modules avancés
            s'ouvrent quand ton commerce grandit.
          </p>
        </div>

        <div className="mt-14 overflow-x-auto border-2 border-charcoal-800 shadow-neobrutal-10">
          <table className="w-full min-w-[920px] border-collapse bg-white">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-[34%] min-w-[220px] border-b-2 border-r-2 border-charcoal-800 bg-paper p-4 text-left font-marketing-mono text-[11px] font-bold uppercase tracking-[0.14em] text-charcoal-500">
                  <span className="sr-only">Fonctionnalité</span>
                </th>
                {PLANS.map((p, i) => (
                  <th
                    key={p}
                    className={clsx(
                      'border-b-2 border-r-2 border-charcoal-800 p-4 text-left align-top',
                      i === 4 && 'border-r-0',
                      i === 2 && 'bg-lime-500/20',
                    )}
                  >
                    <div className="font-marketing-display text-[16px] font-bold text-charcoal-800 md:text-[18px]">
                      {p}
                    </div>
                    <div className="mt-1 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.1em] text-charcoal-600">
                      {PRICES[i]}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((cat) => (
                <Fragment key={cat.title}>
                  <tr>
                    <td
                      colSpan={6}
                      className="border-b-2 border-charcoal-800 bg-charcoal-800 p-3 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.16em] text-lime-400"
                    >
                      {cat.title}
                    </td>
                  </tr>
                  {cat.rows.map((r) => (
                    <tr key={`${cat.title}-${r.label}`}>
                      <td className="sticky left-0 z-10 border-b-2 border-r-2 border-charcoal-800 bg-paper p-4 font-marketing-body text-[14px] font-semibold text-charcoal-800">
                        {r.label}
                      </td>
                      {r.values.map((v, i) => (
                        <td
                          key={i}
                          className={clsx(
                            'border-b-2 border-r-2 border-charcoal-800 p-4',
                            i === 4 && 'border-r-0',
                            i === 2 && 'bg-lime-500/10',
                          )}
                        >
                          <CellContent value={v} featured={i === 2} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
